import { memo, type HTMLAttributes } from "react"
import { useSnapCarousel } from "@/hooks/use-snap-carousel"
import { useSupportsHover } from "@/hooks/use-supports-hover"
import { InspirationSummary } from "@/types/inspiration"
import { cn } from "cn"

import { EntitySheetTrackingContext } from "@/lib/storage/detail-sheet-slice"
import IconChevronLeft from "@/components/icons/chevron-left"
import IconChevronRight from "@/components/icons/chevron-right"
import { InspirationCard } from "./inspiration-card"
import { IconButton } from "./ui/icon-button"
import { Link } from "./ui/link"

export function InspirationCarousel({
  inspiration,
  className,
  loading,
  onMoreHref,
  entitySheetTrackingContext,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  inspiration: (InspirationSummary | undefined)[]
  loading?: boolean
  onMoreHref?: string
  entitySheetTrackingContext: EntitySheetTrackingContext
}) {
  const supportsHover = useSupportsHover()
  const { scrollRef, pages, activePageIndex, prev, next } = useSnapCarousel({
    trackPages: supportsHover,
    contentKey: !loading,
  })

  return (
    <div
      className={cn(
        "@container/carousel [--carousel-gap:theme(space[2.5])] [--carousel-item-size:theme(space.48)]",
        className
      )}
      {...props}
    >
      <div
        data-slot="carousel-scroll-wrap"
        className="group/carousel relative -mr-(--carousel-outdent-r) -ml-(--carousel-outdent-l) overflow-hidden"
      >
        <ul
          ref={scrollRef}
          className="scrollbar-hidden flex snap-x snap-mandatory scroll-pl-(--carousel-outdent-l) gap-(--carousel-gap) overflow-x-auto overflow-y-hidden overscroll-x-contain *:shrink-0 *:not-first:not-last:basis-(--carousel-item-size) *:not-first:not-last:snap-start"
        >
          <li className="-ml-(--carousel-gap) w-(--carousel-outdent-l) shrink-0" />
          {inspiration.map((summary, index) => (
            <InspirationCarouselItem
              key={summary?.entity_ref ?? index}
              inspiration={summary}
              loading={loading}
              entitySheetTrackingContext={entitySheetTrackingContext}
            />
          ))}
          {!!onMoreHref && (
            <li>
              <Link
                href={onMoreHref}
                className="flex aspect-portrait flex-col items-center justify-center rounded-2xl bg-gray-3 py-3 text-gray-10"
              >
                <span className="my-auto flex size-12 items-center justify-center rounded-full border-[3px] border-current">
                  <IconChevronRight
                    size={28}
                    strokeWidth={2.5}
                    className="-mr-1"
                  />
                </span>
                <span className="text-2xs font-semibold">See more</span>
              </Link>
            </li>
          )}
          <li className="-ml-(--carousel-gap) w-(--carousel-outdent-r) shrink-0" />
        </ul>
        {pages.length > 1 && (
          <>
            <div
              className={cn(
                "absolute top-20 left-2 hidden opacity-0 transition-opacity group-hover/carousel:opacity-100 can-hover:block",
                activePageIndex === 0 && "pointer-events-none opacity-0!"
              )}
            >
              <IconButton
                size="md"
                icon={IconChevronLeft}
                variant="outline"
                label="Previous"
                onClick={prev}
                className="bg-background"
              />
            </div>
            <div
              className={cn(
                "absolute top-20 right-2 hidden opacity-0 transition-opacity group-hover/carousel:opacity-100 can-hover:block",
                activePageIndex === pages.length - 1 &&
                  "pointer-events-none opacity-0!"
              )}
            >
              <IconButton
                size="md"
                icon={IconChevronRight}
                variant="outline"
                label="Next"
                onClick={next}
                className="bg-background"
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const InspirationCarouselItem = memo(function InspirationCarouselItem({
  inspiration,
  loading,
  entitySheetTrackingContext,
}: {
  inspiration?: InspirationSummary
  loading?: boolean
  entitySheetTrackingContext: EntitySheetTrackingContext
}) {
  if (loading || !inspiration) {
    return (
      <li>
        <InspirationCard
          minimal
          trackingContext="CardCarousel"
          entitySheetTrackingContext={entitySheetTrackingContext}
        />
      </li>
    )
  }

  return (
    <li className="relative flex flex-col">
      <InspirationCard
        inspiration={inspiration}
        minimal
        trackingContext="CardCarousel"
        entitySheetTrackingContext={entitySheetTrackingContext}
      />
    </li>
  )
})
